import { Observable } from 'rxjs';
import type { FetchEventSourceInit } from '@microsoft/fetch-event-source';
import { fetchEventSource } from '@microsoft/fetch-event-source';

// Wraps fetchEventSource in an RxJS Observable. fetchEventSource's built-in retry is
// disabled (onerror throws) because the backend sends no SSE event IDs, so a native
// reconnect would restart the stream and produce duplicate content. Reconnection is
// handled by handleStreamError in useAgentsChats.ts instead.
export const createSSEObservable = (url: string, options: RequestInit): Observable<string> => {
  return new Observable((subscriber) => {
    const controller = new AbortController();

    const eventSourceInit: FetchEventSourceInit = {
      ...options,
      openWhenHidden: true,
      credentials: 'include',
      signal: controller.signal,
      headers: options.headers
        ? Object.fromEntries(new Headers(options.headers).entries())
        : undefined,
      onmessage(msg) {
        if (msg.event === 'streamNotFound') {
          subscriber.error(msg);
          controller.abort();
        }
        subscriber.next(msg.data);
      },
      onerror(err) {
        subscriber.error(err);
        controller.abort();
        // Throwing prevents fetchEventSource from scheduling its own retry — reconnection
        // is owned by handleStreamError. Without this, fetchEventSource retries race
        // against handleStreamError and may consume the backend stream first.
        throw err;
      },
      onclose() {
        subscriber.complete();
      },
    };

    // Suppress the unhandled rejection caused by onerror throwing above — the error is
    // already forwarded to the subscriber.
    fetchEventSource(url, eventSourceInit).catch(() => {});

    return () => {
      controller.abort();
    };
  });
};
